import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import { useAppSelector } from '@verantos-dev/core-annotation-tool';

import {
  useGetAnnotationProgressBatchWiseQuery,
  useGetAnnotationProgressQuery,
} from './api/metricsApiSlice';
import { getAllAnnotatorsOption } from './constants';
import {
  selectBatchwiseProgressEndDate,
  selectBatchwiseProgressStartDate,
  selectSelectedAnnotatorId,
  selectSelectedProjectId,
} from './state/selector';
import { AnnotationProgress, BatchwiseAnnotationProgress } from './types/responseTypes';

interface useMetricDashboardResult {
  annotationProgressData: AnnotationProgress | null;
  isAnnotationProgressDataLoading: boolean;
  annotationProgressBatchWiseData: BatchwiseAnnotationProgress[] | [];
  isAnnotationProgressBatchWiseDataLoading: boolean;
  selectedProjectId: string | null;
}
export const useMetricDashboard = (): useMetricDashboardResult => {
  const selectedAnnotatorId = useAppSelector(selectSelectedAnnotatorId);
  const { formatMessage } = useIntl();
  const allAnnotatorOption = getAllAnnotatorsOption(formatMessage);

  const selectedProjectId = useAppSelector(selectSelectedProjectId);
  const {
    data: annotationProgressData,
    isLoading: isAnnotationProgressDataLoading,
    isFetching: isAnnotationProgressDataFetching,
  } = useGetAnnotationProgressQuery(
    {
      userId:
        selectedAnnotatorId === allAnnotatorOption.id
          ? undefined
          : selectedAnnotatorId ?? undefined,
      projectId: selectedProjectId || '',
    },
    { skip: !selectedProjectId }
  );

  const batchWiseStartDate = useAppSelector(selectBatchwiseProgressStartDate);
  const batchWiseEndDate = useAppSelector(selectBatchwiseProgressEndDate);

  const {
    data: annotationProgressBatchWiseData,
    isLoading: isAnnotationProgressBatchWiseDataLoading,
    isFetching: isAnnotationProgressBatchWiseDataFetching,
  } = useGetAnnotationProgressBatchWiseQuery(
    {
      userId:
        selectedAnnotatorId === allAnnotatorOption.id
          ? undefined
          : selectedAnnotatorId ?? undefined,
      projectId: selectedProjectId || '',
      startDate: batchWiseStartDate ? dayjs(batchWiseStartDate).format('YYYY-MM-DD') : undefined,
      endDate: batchWiseEndDate ? dayjs(batchWiseEndDate).format('YYYY-MM-DD') : undefined,
    },
    { skip: !selectedProjectId }
  );

  return {
    annotationProgressData: annotationProgressData?.data || null,
    isAnnotationProgressDataLoading:
      isAnnotationProgressDataFetching || isAnnotationProgressDataLoading,
    annotationProgressBatchWiseData: annotationProgressBatchWiseData?.data || [],
    isAnnotationProgressBatchWiseDataLoading:
      isAnnotationProgressBatchWiseDataLoading || isAnnotationProgressBatchWiseDataFetching,
    selectedProjectId,
  };
};
